import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { IJobBoardFirm } from '@/services/firm/IFirms'

const resource = 'managements'

export type TCypressE2eStatus = 'queued' | 'running' | 'passed' | 'failed' | 'cancelled' | 'unknown' | 'error'
export type TCypressE2eTriggerSource = 'manual' | 'cron' | 'sync'

export type TCypressE2eTest = {
  fullTitle?: string | null
  title?: string | null
  state?: string | null
  startedAt?: string | null
  finishedAt?: string | null
  durationMs?: number | null
  attempt?: number | null
  url?: string | null
  error?: string | null
  group?: string | null
}

export type TCypressE2eTestsSummary = {
  total?: number
  passed?: number
  failed?: number
  pending?: number
}

export type TCypressE2eRun = {
  _id: string
  remoteRunId?: string | null
  status: TCypressE2eStatus
  exitCode?: number | null
  baseUrl?: string | null
  triggerSource: TCypressE2eTriggerSource
  triggeredByEmail?: string | null
  startedAt?: string | null
  finishedAt?: string | null
  durationMs?: number | null
  historyUrl?: string | null
  logUrl?: string | null
  screenshotsUrl?: string | null
  videosUrl?: string | null
  resultsUrl?: string | null
  screenshotsCount?: number
  videosCount?: number
  hasLog?: boolean
  hasResults?: boolean
  testsSummary?: TCypressE2eTestsSummary | null
  tests?: TCypressE2eTest[]
  errorMessage?: string | null
  logTail?: string | null
  createdAt?: string
  updatedAt?: string
}

export type TCypressE2eListResponse = {
  configured: boolean
  historyUrl: string | null
  remoteStatus: { running?: boolean; syncError?: string }
  runs: TCypressE2eRun[]
}

const getServers = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/manage/servers`,
  })
}

const updateServers = (axios: IFactoryFunctionRequest<any, any>) => (data: any): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/manage/servers`,
    data,
  })
}

const listCypressE2eRuns = (axios: IFactoryFunctionRequest<TCypressE2eListResponse, any>) => (params?: { sync?: boolean; limit?: number }): IFactoryResponse<TCypressE2eListResponse, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/cypress-e2e/runs`,
    query: params,
  })
}

const getCypressE2eRun = (axios: IFactoryFunctionRequest<TCypressE2eRun, any>) => (id: string): IFactoryResponse<TCypressE2eRun, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/cypress-e2e/runs/${id}`,
  })
}

const getCypressE2eStatus = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/cypress-e2e/status`,
  })
}

const syncCypressE2eRuns = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/cypress-e2e/sync`,
  })
}

const triggerCypressE2eRun = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/cypress-e2e/trigger`,
  })
}

const cancelCypressE2eRun = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/cypress-e2e/cancel`,
  })
}

export default (axios: AxiosInstance) => ({
  getServers: getServers(useAxios(axios)),
  updateServers: updateServers(useAxios(axios)),
  listCypressE2eRuns: listCypressE2eRuns(useAxios(axios)),
  getCypressE2eRun: getCypressE2eRun(useAxios(axios)),
  getCypressE2eStatus: getCypressE2eStatus(useAxios(axios)),
  syncCypressE2eRuns: syncCypressE2eRuns(useAxios(axios)),
  triggerCypressE2eRun: triggerCypressE2eRun(useAxios(axios)),
  cancelCypressE2eRun: cancelCypressE2eRun(useAxios(axios)),
})
